import {createCraftMeasurement} from '../craft/measurement.js';
import {timezoneSafeTimestamp} from '../core/time.js';
import {temperatureToCelsius} from '../journal/stage-target.js';

export const TemperatureReadingKind=Object.freeze({
  CHAMBER:'chamber',
  INTERNAL:'internal'
});

export const TemperatureReadingSource=Object.freeze({
  MANUAL:'manual',
  THERMOMETER_PROBE:'thermometer_probe',
  DEVICE:'device'
});

export const TemperatureTransport=Object.freeze({
  BLUETOOTH:'bluetooth',
  WIFI:'wifi',
  WIRED:'wired',
  OTHER:'other'
});

const KINDS=new Set(Object.values(TemperatureReadingKind));
const SOURCES=new Set(Object.values(TemperatureReadingSource));
const TRANSPORTS=new Set(Object.values(TemperatureTransport));

function required(value,label,max=160){
  const text=String(value||'').trim();
  if(!text)throw new TypeError(label+' is required');
  if(text.length>max)throw new TypeError(label+' is too long');
  return text;
}

function optional(value,label,max=160){
  if(value==null||value==='')return null;
  return required(value,label,max);
}

function enumValue(value,label,allowed){
  const key=String(value||'').trim().toLowerCase();
  if(!allowed.has(key))throw new TypeError('Unsupported '+label);
  return key;
}

function optionalEnum(value,label,allowed){
  if(value==null||value==='')return null;
  return enumValue(value,label,allowed);
}

function timestamp(value,label){
  return timezoneSafeTimestamp(value,label);
}

function unit(value){
  const key=String(value||'').trim().toUpperCase();
  if(!['C','F'].includes(key))throw new TypeError('enteredUnit must be C or F');
  return key;
}

function numeric(value,label){
  const number=Number(value);
  if(!Number.isFinite(number))throw new TypeError(label+' must be numeric');
  return number;
}

function idOrGenerated(value){
  const id=String(value||'').trim();
  if(id)return required(id,'id',128);
  const generated=globalThis.crypto?.randomUUID?.();
  if(generated)return generated;
  throw new TypeError('id is required when crypto.randomUUID is unavailable');
}

function assertTemperature(kind,valueCelsius){
  const [min,max]=kind===TemperatureReadingKind.CHAMBER?[-100,600]:[-100,300];
  if(valueCelsius<min||valueCelsius>max){
    throw new TypeError('Temperature is outside the supported '+kind+' range');
  }
}

export function createTemperatureReading({
  id,
  userId,
  sessionId,
  readingKind,
  recordedAt,
  enteredValue,
  enteredUnit='C',
  itemId=null,
  probeId=null,
  sourceType=TemperatureReadingSource.MANUAL,
  sourceTransport=null,
  sourceRef=null,
  correctsReadingId=null,
  correctionNote=null
}={}){
  const readingId=idOrGenerated(id);
  const kind=enumValue(readingKind,'temperature reading kind',KINDS);
  const source=enumValue(sourceType,'temperature source type',SOURCES);
  const transport=optionalEnum(sourceTransport,'temperature transport',TRANSPORTS);
  const originalUnit=unit(enteredUnit);
  const originalValue=numeric(enteredValue,'enteredValue');
  const valueCelsius=temperatureToCelsius(originalValue,{unit:originalUnit});
  assertTemperature(kind,valueCelsius);

  const item=optional(itemId,'itemId',128);
  const probe=optional(probeId,'probeId',128);
  const sourceReference=optional(sourceRef,'sourceRef',160);
  const correction=optional(correctsReadingId,'correctsReadingId',128);
  const correctionText=optional(correctionNote,'correctionNote',500);

  if(correction===readingId)throw new TypeError('A temperature reading cannot correct itself');
  if(correction&&!correctionText)throw new TypeError('correctionNote is required for a correction');
  if(!correction&&correctionText)throw new TypeError('correctionNote requires correctsReadingId');
  if(source===TemperatureReadingSource.THERMOMETER_PROBE&&!probe){
    throw new TypeError('probeId is required for thermometer_probe source');
  }
  if(source===TemperatureReadingSource.DEVICE&&!sourceReference){
    throw new TypeError('sourceRef is required for device source');
  }

  return Object.freeze({
    id:readingId,
    userId:required(userId,'userId',128),
    sessionId:required(sessionId,'sessionId',128),
    readingKind:kind,
    recordedAt:timestamp(recordedAt,'recordedAt'),
    enteredValue:originalValue,
    enteredUnit:originalUnit,
    valueCelsius,
    itemId:item,
    probeId:probe,
    sourceType:source,
    sourceTransport:transport,
    sourceRef:sourceReference,
    correctsReadingId:correction,
    correctionNote:correctionText
  });
}

export function temperatureReadingToRow(reading){
  const value=createTemperatureReading(reading);
  return Object.freeze({
    id:value.id,
    user_id:value.userId,
    session_id:value.sessionId,
    reading_kind:value.readingKind,
    recorded_at:value.recordedAt,
    entered_value:value.enteredValue,
    entered_unit:value.enteredUnit,
    value_c:value.valueCelsius,
    item_id:value.itemId,
    probe_id:value.probeId,
    source_type:value.sourceType,
    source_transport:value.sourceTransport,
    source_ref:value.sourceRef,
    corrects_reading_id:value.correctsReadingId,
    correction_note:value.correctionNote
  });
}

export function temperatureReadingFromRow(row={}){
  return createTemperatureReading({
    id:row.id,
    userId:row.user_id,
    sessionId:row.session_id,
    readingKind:row.reading_kind,
    recordedAt:row.recorded_at,
    enteredValue:row.entered_value,
    enteredUnit:row.entered_unit,
    itemId:row.item_id,
    probeId:row.probe_id,
    sourceType:row.source_type,
    sourceTransport:row.source_transport,
    sourceRef:row.source_ref,
    correctsReadingId:row.corrects_reading_id,
    correctionNote:row.correction_note
  });
}

export function orderTemperatureReadings(readings=[]){
  if(!Array.isArray(readings))throw new TypeError('readings must be an array');
  return Object.freeze([...readings]
    .map(createTemperatureReading)
    .sort((a,b)=>Date.parse(a.recordedAt)-Date.parse(b.recordedAt)||String(a.id).localeCompare(String(b.id))));
}

export function temperatureReadingToMeasurement(reading){
  const value=createTemperatureReading(reading);
  return createCraftMeasurement({
    id:value.id,kind:'temperature',unit:'C',value:value.valueCelsius,recordedAt:value.recordedAt,
    subjectId:value.itemId||value.sessionId,source:{kind:value.sourceType,ref:value.sourceRef||value.probeId}
  });
}
